/**
 * Material Symbols Outlined glyph wrapper.
 *
 * Renders a named Material Symbols glyph (e.g. `dashboard`, `logout`,
 * `account_circle`) by emitting the glyph name as ligature text in the
 * self-hosted Material Symbols Outlined variable font. The font is registered
 * by `./fonts` and its family name is `MATERIAL_SYMBOLS_FONT`.
 *
 * Sizing/color follow the surrounding text (`fontSize: 'inherit'`,
 * `color: 'inherit'`) unless overridden via `sx`.
 *
 *   <Icon name="dashboard" />
 *   <Icon name="logout" sx={{ fontSize: 20, color: 'primary.main' }} />
 */
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { MATERIAL_SYMBOLS_FONT } from './tokens';

export interface IconProps {
  /** Material Symbols Outlined glyph name (the ligature text), e.g. `dashboard`. */
  name: string;
  /** Optional style overrides (size, color, weight axis, etc.). */
  sx?: SxProps<Theme>;
  /**
   * Accessible label. When omitted the glyph is decorative (`aria-hidden`),
   * which is the right default beside a visible text label.
   */
  title?: string;
}

export function Icon({ name, sx, title }: IconProps) {
  const decorative = title === undefined;
  return (
    <Box
      component="span"
      // The ligature font turns the glyph name into the icon outline.
      aria-hidden={decorative ? true : undefined}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : title}
      sx={[
        {
          fontFamily: MATERIAL_SYMBOLS_FONT,
          fontWeight: 'normal',
          fontStyle: 'normal',
          fontSize: 'inherit',
          lineHeight: 1,
          letterSpacing: 'normal',
          textTransform: 'none',
          display: 'inline-block',
          whiteSpace: 'nowrap',
          wordWrap: 'normal',
          direction: 'ltr',
          // Render the named ligature rather than the literal text.
          fontFeatureSettings: "'liga'",
          WebkitFontSmoothing: 'antialiased',
          userSelect: 'none',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {name}
    </Box>
  );
}

export default Icon;
